import { Trans, useTranslation } from 'next-i18next'

import { useStateData } from '@/lib/locations'
import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'
import useFilterSlug from '@/features/Listings/seo/search-results-content/content/use-filter-slug'

const GenericCityContent = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const stateData = useStateData(state)
  const { name, shortName, slug } = stateData
  const { hasFilter, filterName } = useFilterSlug(filterSlug)

  const links = getFilterLinks(isCanadaPage, citySlug)

  const getAbout = () => {
    if (isCanadaPage)
      return (
        <Paragraph>
          <Trans i18nKey="seo:About Canada Paragraph">
            With ten diverse provinces and three territories,{' '}
            <LinkText
              href={
                isChinese
                  ? 'https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e5%8a%a0%e6%8b%bf%e5%a4%a7%e7%95%99%e5%ad%a65%e5%a4%a7%e5%9f%8e%e5%b8%82-%e7%a7%9f%e6%88%bf%e3%80%81%e5%ad%a6%e8%b4%b9%e3%80%81%e5%b7%a5%e4%bd%9c%e3%80%81%e7%ad%be%e8%af%81%e8%af%a6%e8%a7%a3/'
                  : 'https://liv.rent/blog/renters/best-canadian-cities-for-immigration/'
              }
            >
              Canada is a vast country that offers plenty of opportunities
            </LinkText>{' '}
            for everything from tourism to employment & education.{' '}
            <LinkText href="/rental-listings/ontario">Ontario</LinkText> and{' '}
            <LinkText href="/rental-listings/british-columbia">BC</LinkText> are
            two of the most populous provinces, and also some of the most
            in-demand – with notoriously high rent prices to match. Their main
            cities,{' '}
            <LinkText href="/rental-listings/city/toronto">Toronto</LinkText>{' '}
            and{' '}
            <LinkText href="/rental-listings/city/vancouver">
              Vancouver
            </LinkText>{' '}
            respectively, are two of the most popular places in the country for
            renters in particular, offering unique exposure to Canada’s famous
            natural splendour as well as plentiful employment opportunities and
            world-class academic institutions.
          </Trans>
        </Paragraph>
      )
    switch (state) {
      case 'BC':
        return (
          <Paragraph>
            <Trans i18nKey="seo:About BC Paragraph">
              {{ cityName }} is a city in{' '}
              <LinkText href="/rental-listings/british-columbia">
                British Columbia (BC)
              </LinkText>{' '}
              - a province in Western Canada that is known for its breathtaking
              natural beauty and diverse urban centres. Every city in BC has its
              own unique character and attractions, from cultural festivities to
              world-class shopping and dining. Overall, cities in BC offer a
              diverse range of experiences. For more information, you can refer
              to our blog to learn more about the{' '}
              <LinkText
                href={
                  isChinese
                    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e5%8d%91%e8%af%97%e7%9c%81-%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/'
                    : 'https://liv.rent/blog/neighbourhoods/british-columbia-neighbourhoods/'
                }
              >
                cities and neighbourhoods in BC
              </LinkText>
              .
            </Trans>
          </Paragraph>
        )
      case 'ON':
        return (
          <Paragraph>
            <Trans i18nKey="seo:About ON Paragraph">
              {{ cityName }} is a city in{' '}
              <LinkText href="/rental-listings/ontario">Ontario (ON)</LinkText>{' '}
              - the most populous province in Canada that is known for offering
              a rich mix of urban excitement, natural beauty, and economic
              opportunity. Every city in Ontario has its own unique culture,
              history, and attractions to offer visitors and residents alike.
              For more information, you can refer to our blog to learn more
              about the{' '}
              <LinkText
                href={
                  isChinese
                    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e5%ae%89%e5%a4%a7%e7%95%a5%e7%9c%81-%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/'
                    : 'https://liv.rent/blog/neighbourhoods/ontario-neighbourhoods/'
                }
              >
                cities and neighbourhoods in Ontario
              </LinkText>
              .
            </Trans>
          </Paragraph>
        )
      case 'QC':
        return (
          <Paragraph>
            <Trans i18nKey="seo:About QC Paragraph">
              {{ cityName }} is a city in{' '}
              <LinkText href="/rental-listings/quebec">Quebec (QC)</LinkText> -
              a province in Eastern Canada that is known for its rich history,
              distinctive culture, and stunning natural beauty. Every city in
              Quebec has its own unique character and charm, from thriving arts
              and culture scene to distinctive architecture. Overall, cities in
              Quebec offer a unique blend of European charm and North American
              energy. For more information, you can refer to our blog to learn
              more about the{' '}
              <LinkText
                href={
                  isChinese
                    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/'
                    : 'https://liv.rent/blog/neighbourhoods/'
                }
              >
                cities and neighbourhoods in Canada
              </LinkText>
              .
            </Trans>
          </Paragraph>
        )
      case 'AB':
        return (
          <Paragraph>
            <Trans i18nKey="seo:About AB Paragraph">
              {{ cityName }} is a city in{' '}
              <LinkText href="/rental-listings/alberta">Alberta (AB)</LinkText>{' '}
              - a province located in Western Canada that is known for its
              stunning natural beauty. Every city in Alberta has its own unique
              character, history, and attractions to offer visitors and
              residents alike. Overall, cities in Alberta offer a rich mix of
              natural beauty, urban excitement, and economic opportunity. For
              more information, you can refer to our blog to learn more about
              the{' '}
              <LinkText
                href={
                  isChinese
                    ? 'https://liv.rent/blog/zh/%E8%A1%97%E5%8C%BA%E6%8C%87%E5%8D%97/%E9%98%BF%E5%B0%94%E4%BC%AF%E5%A1%94%E7%9C%81-%E8%A1%97%E5%8C%BA%E6%8C%87%E5%8D%97/'
                    : 'https://liv.rent/blog/neighbourhoods/alberta-neighbourhoods/'
                }
              >
                cities and neighbourhoods in Alberta
              </LinkText>
              .
            </Trans>
          </Paragraph>
        )
      default:
        return (
          <Paragraph>
            <Trans i18nKey="seo:About Default Paragraph">
              {{ cityName }} is a city in{' '}
              <LinkText href={`/rental-listings/${slug}`}>
                {{ name }} ({{ shortName }})
              </LinkText>{' '}
              - a province known for its stunning natural beauty, dynamic
              cities, and thriving economy. Every city in {{ name }} has its own
              unique character, history, and attractions to offer visitors and
              residents alike. For more information, you can refer to our blog
              to learn more about the{' '}
              <LinkText
                href={
                  isChinese
                    ? 'https://liv.rent/blog/zh/%E8%A1%97%E5%8C%BA%E6%8C%87%E5%8D%97/'
                    : 'https://liv.rent/blog/neighbourhoods/'
                }
              >
                cities and neighbourhoods in Canada
              </LinkText>
              .
            </Trans>
          </Paragraph>
        )
    }
  }

  const getRentingIn = () => {
    const filterNameWithFallback = hasFilter
      ? filterName
      : t('seo:an apartment to rent')

    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo:Renting FILTER in LOCATION in Canada Paragraph">
            Finding {{ filterNameWithFallback }} in {{ cityName }} requires
            research, communication with{' '}
            <LinkText href="/landlords">landlords & property managers</LinkText>
            , securing references, and signing a lease. All this can add up to a
            lengthy endeavour if you’re not familiar with the rental process in
            Canada. To help you get started, here is a list of resources with
            tips on renting in Canada.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e8%b5%81%e6%b3%95/%e5%8a%a0%e6%8b%bf%e5%a4%a74%e7%9c%81%e7%a7%9f%e6%88%bf%e6%8a%bc%e9%87%91%e3%80%81%e7%bb%ad%e7%ba%a6%e3%80%81%e7%a7%9f%e9%87%91%e6%b6%a8%e5%b9%85%e3%80%81%e6%94%af%e4%bb%98%e6%9d%a1%e6%ac%be%e6%af%94/">
              如何在加拿大租房？
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/liv%E7%A7%9F%E6%88%BF/%E5%9C%A8%E5%8A%A0%E6%8B%BF%E5%A4%A71000-%E6%9C%88%E8%83%BD%E7%A7%9F%E5%88%B0%E4%BB%80%E4%B9%88%E6%A0%B7%E7%9A%84%E6%88%BF/">
              租$1000可以在加拿大租到什么房？
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/renters/how-does-rent-work-in-canada-a-provincial-comparison/">
              How does rent work in Canada?
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/">
              What $1800 in rent can get you across Canada
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/renters/rent-in-canada-how-to-secure-a-home-before-coming-to-canada/">
              How to secure a home before arriving in Canada
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getAverageRent = () => {
    if (isCanadaPage) {
      return (
        <>
          <Section3>
            <Heading3>
              {hasFilter ? (
                <Trans
                  i18nKey="seo:What is the average rent in LOCATION for FILTER?"
                  values={{
                    filter: filterName,
                    location: cityName
                  }}
                  defaults="What is the average rent in {{ location }} for {{ filter }}?"
                />
              ) : (
                <Trans
                  i18nKey="seo:What is the average rent in LOCATION?"
                  values={{
                    location: cityName
                  }}
                  defaults="What is the average rent in {{ location }}?"
                />
              )}
            </Heading3>
            <Paragraph>
              <Trans i18nKey="seo:Average Rent Paragraph Canada">
                The average rent in Canada will vary by province and by city.
                For the latest stats on average rent in Canada, refer to our{' '}
                <LinkText
                  href={
                    isChinese
                      ? 'https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/'
                      : 'https://liv.rent/blog/rent-reports/'
                  }
                >
                  monthly rent reports
                </LinkText>
                .
              </Trans>
            </Paragraph>
          </Section3>
          <Section3>
            <Heading3>
              {t('seo:What is the average rent in Vancouver?')}
            </Heading3>
            <Paragraph>
              <Trans i18nKey="seo:Average Rent Paragraph Vancouver">
                The average rent for a{' '}
                <LinkText href="/rental-listings/city/vancouver/1-bedroom-apartments">
                  1 bedroom apartment in Vancouver, BC
                </LinkText>{' '}
                is between $2,000 - $2,600. To stay up to date on the{' '}
                <LinkText href="https://liv.rent/blog/rent-reports/vancouver/">
                  average rent in Vancouver
                </LinkText>
                , check out our monthly{' '}
                <LinkText
                  href={
                    isChinese
                      ? 'https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E6%B8%A9%E5%93%A5%E5%8D%8E/'
                      : 'https://liv.rent/blog/rent-reports/vancouver/'
                  }
                >
                  Vancouver rent reports
                </LinkText>
                .
              </Trans>
            </Paragraph>
          </Section3>
          <Section3>
            <Heading3>{t('seo:What is the average rent in Toronto?')}</Heading3>
            <Paragraph>
              <Trans i18nKey="seo:Average Rent Paragraph Toronto">
                The average rent for a{' '}
                <LinkText href="/rental-listings/city/toronto/1-bedroom-apartments">
                  1 bedroom apartment in Toronto, ON
                </LinkText>{' '}
                is between $2,000 - $2,200. To stay up to date on the{' '}
                <LinkText href="https://liv.rent/blog/rent-reports/toronto">
                  average rent in Toronto
                </LinkText>
                , check out our monthly{' '}
                <LinkText
                  href={
                    isChinese
                      ? 'https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E5%A4%9A%E4%BC%A6%E5%A4%9A/'
                      : 'https://liv.rent/blog/rent-reports/toronto'
                  }
                >
                  Toronto rent reports
                </LinkText>
                .
              </Trans>
            </Paragraph>
          </Section3>
          <Section3>
            <Heading3>
              {t('seo:What is the average rent in Montreal?')}
            </Heading3>
            <Paragraph>
              <Trans i18nKey="seo:Average Rent Paragraph Montreal">
                The average rent for a{' '}
                <LinkText href="/rental-listings/city/montreal/1-bedroom-apartments">
                  1 bedroom apartment in Montreal, QC
                </LinkText>{' '}
                is between $1,200 - $1,600. To stay up to date on the{' '}
                <LinkText href="https://liv.rent/blog/rent-reports/montreal">
                  average rent in Montreal
                </LinkText>
                , check out our monthly{' '}
                <LinkText
                  href={
                    isChinese
                      ? 'https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E8%92%99%E7%89%B9%E5%88%A9%E5%B0%94/'
                      : 'https://liv.rent/blog/rent-reports/montreal'
                  }
                >
                  Montreal rent reports
                </LinkText>
                .
              </Trans>
            </Paragraph>
          </Section3>
          <Section3>
            <Heading3>{t('seo:What is the average rent in Calgary?')}</Heading3>
            <Paragraph>
              <Trans i18nKey="seo:Average Rent Paragraph Calgary">
                The average rent for a{' '}
                <LinkText href="/rental-listings/city/calgary/1-bedroom-apartments">
                  1 bedroom apartment in Calgary, AB
                </LinkText>{' '}
                is between $1,400 - $1,500. To stay up to date on the{' '}
                <LinkText href="https://liv.rent/blog/rent-reports/alberta">
                  average rent in Calgary
                </LinkText>
                , check out our monthly{' '}
                <LinkText
                  href={
                    isChinese
                      ? 'https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E9%98%BF%E5%B0%94%E4%BC%AF%E5%A1%94/'
                      : 'https://liv.rent/blog/rent-reports/alberta'
                  }
                >
                  Calgary rent reports
                </LinkText>
                .
              </Trans>
            </Paragraph>
          </Section3>
          <Section3>
            <Heading3>
              {t('seo:What is the average rent in Edmonton?')}
            </Heading3>
            <Paragraph>
              <Trans i18nKey="seo:Average Rent Paragraph Edmonton">
                The average rent for a{' '}
                <LinkText href="/rental-listings/city/edmonton/1-bedroom-apartments">
                  1 bedroom apartment in Edmonton, AB
                </LinkText>{' '}
                is between $1,000 - $1,100. To stay up to date on the{' '}
                <LinkText href="https://liv.rent/blog/rent-reports/alberta">
                  average rent in Edmonton
                </LinkText>
                , check out our monthly{' '}
                <LinkText
                  href={
                    isChinese
                      ? 'https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E9%98%BF%E5%B0%94%E4%BC%AF%E5%A1%94/'
                      : 'https://liv.rent/blog/rent-reports/alberta'
                  }
                >
                  Edmonton rent reports
                </LinkText>
                .
              </Trans>
            </Paragraph>
          </Section3>
        </>
      )
    }
    return (
      <Section3>
        <Heading3>
          {hasFilter ? (
            <Trans
              i18nKey="seo:What is the average rent in LOCATION for FILTER?"
              values={{
                filter: filterName,
                location: cityName
              }}
              defaults="What is the average rent in {{ location }} for {{ filter }}?"
            />
          ) : (
            <Trans
              i18nKey="seo:What is the average rent in LOCATION?"
              values={{
                location: cityName
              }}
              defaults="What is the average rent in {{ location }}?"
            />
          )}
        </Heading3>
        <Paragraph>
          {hasFilter ? (
            <Trans i18nKey="seo:Average rent in LOCATION for FILTER paragraph">
              The average rent in {{ cityNameWithState }} for {{ filterName }}{' '}
              will vary on a month-to-month basis. For the latest stats on
              average rent in {{ cityName }}, refer to our{' '}
              <LinkText href="https://liv.rent/blog/rent-reports/">
                monthly rent reports
              </LinkText>
              .
            </Trans>
          ) : (
            <Trans i18nKey="seo:Average rent in LOCATION paragraph">
              The average rent in {{ cityNameWithState }} will vary on a
              month-to-month basis. For the latest stats on average rent in{' '}
              {{ cityName }}, refer to our{' '}
              <LinkText
                href={
                  isChinese
                    ? 'https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/'
                    : 'https://liv.rent/blog/rent-reports/'
                }
              >
                monthly rent reports
              </LinkText>
              .
            </Trans>
          )}
        </Paragraph>
      </Section3>
    )
  }

  const getApartmentHunting = () => {
    const filterNameWithFallback = hasFilter
      ? filterName
      : t('seo:cheap rentals')

    if (isCanadaPage) {
      return (
        <>
          <Paragraph>
            {hasFilter ? (
              <Trans i18nKey="seo:Apartment Hunting FILTER in Canada Paragraph">
                Finding {{ filterName }} in Canada may be a challenge depending
                on where you’re looking to rent. To increase your chances of
                finding an affordable home, familiarize yourself with the
                different neighbourhoods and the average rent in each area. Here
                are some neighbourhood guides to help you get started:
              </Trans>
            ) : (
              <Trans i18nKey="seo:Apartment Hunting in Canada Paragraph">
                Finding cheap rentals in Canada may be a challenge depending on
                where you’re looking to rent. If you’re trying to find{' '}
                <LinkText href={links.under1000}>
                  apartments for rent under $1000
                </LinkText>
                , then Vancouver is probably not the best place to look. To
                increase your chances of finding an affordable home, familiarize
                yourself with the different neighbourhoods and the average rent
                in each area. Here are some neighbourhood guides to help you get
                started:
              </Trans>
            )}
          </Paragraph>
          <LinkList>
            <LinkItem
              href={
                isChinese
                  ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/the-best-neighbourhoods-for-families-in-vancouver-cn/'
                  : 'https://liv.rent/blog/neighbourhoods/the-best-neighbourhoods-for-families-in-vancouver/'
              }
            >
              {t('seo:Best neighbourhoods for families in Vancouver')}
            </LinkItem>
            <LinkItem
              href={
                isChinese
                  ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/the-8-best-neighbourhoods-for-families-in-toronto-cn/'
                  : 'https://liv.rent/blog/neighbourhoods/best-neighbourhoods-for-families-in-toronto/'
              }
            >
              {t('seo:Best neighbourhoods for families in Toronto')}
            </LinkItem>
            <LinkItem
              href={
                isChinese
                  ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e7%a7%9f%e6%88%bf%e5%ae%9d%e5%85%b8-%e5%a4%a7%e6%b8%a9%e5%93%a5%e5%8d%8e%e7%a7%9f%e9%87%91%e6%9c%80%e4%be%bf%e5%ae%9c%e7%9a%8410%e4%b8%aa%e5%9c%b0%e5%8c%ba/'
                  : 'https://liv.rent/blog/neighbourhoods/cheapest-neighbourhoods-for-rent-in-metro-vancouver/'
              }
            >
              {t('seo:Cheapest neighbourhoods in Metro Vancouver')}
            </LinkItem>
            <LinkItem
              href={
                isChinese
                  ? 'https://liv.rent/blog/zh/%E7%94%9F%E6%B4%BB%E6%97%B6%E5%B0%9A/%E5%A4%A7%E5%A4%9A%E4%BC%A6%E5%A4%9A8%E5%A4%A7%E5%9F%8E%E5%B8%82%E4%BE%BF%E5%AE%9C%E6%88%BF%E6%BA%90%E6%80%BB%E7%BB%93/'
                  : 'https://liv.rent/blog/neighbourhoods/cheapest-neighbourhoods-for-rent-in-metro-vancouver/'
              }
            >
              {t('seo:Cheapest neighbourhoods in Greater Toronto Area')}
            </LinkItem>
            {!isChinese && (
              <LinkItem href="https://liv.rent/blog/neighbourhoods/cheapest-neighbourhoods-in-the-city-of-toronto/">
                {t('seo:Cheapest neighbourhoods in the City of Toronto')}
              </LinkItem>
            )}
          </LinkList>
          <Paragraph>
            <Trans i18nKey="seo:Apartment Hunting in LOCATION Paragraph 2">
              In addition to choosing your location, it’s important to know what
              rental types are available in {{ cityName }} (e.g.{' '}
              <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
              <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
              <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
              <LinkText href={links.studio}>studio apartments</LinkText>) and
              the average rent for each property type. Refer below to figure out
              your options - ranging from{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
              <LinkText href={links.unfurnished}>unfurnished rentals</LinkText>{' '}
              to <LinkText href={links.longTerm}>long term rentals</LinkText>{' '}
              and <LinkText href={links.shortTerm}>short term rentals</LinkText>
              .
            </Trans>
          </Paragraph>
        </>
      )
    }

    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting FILTER in LOCATION Paragraph 1">
            Finding {{ filterNameWithFallback }} in {{ cityName }} may be a
            challenge depending on where you’re looking to rent. To increase
            your chances of finding an affordable home, familiarize yourself
            with the different neighbourhoods and the average rent in each area.
          </Trans>
          <Trans i18nKey="seo:Apartment Hunting FILTER in LOCATION Paragraph 2">
            For more information, you can refer to our blog to learn more about
            the various{' '}
            <LinkText
              href={
                isChinese
                  ? 'https://liv.rent/blog/zh/%E8%A1%97%E5%8C%BA%E6%8C%87%E5%8D%97/'
                  : 'https://liv.rent/blog/neighbourhoods/'
              }
            >
              neighbourhoods in Canada
            </LinkText>
            .
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting in LOCATION Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available in {{ cityName }} (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    if (isCanadaPage) {
      return (
        <>
          <Section3>
            <Heading3>{t('seo:Apartments for rent')}</Heading3>
            <Paragraph>
              <Trans i18nKey="seo:Apartments for rent Paragraph">
                In urban areas across Canada, apartments are the most common
                form of housing you’ll find. Average rent prices for apartments
                depend on where you’re looking.{' '}
                <LinkText href={links.apartment}>Apartments for rent</LinkText>{' '}
                in urban areas are typically more expensive due to higher
                demand. Apartments generally require much less maintenance than
                houses, making them ideal for renters who don’t need as much
                space.
              </Trans>
            </Paragraph>
          </Section3>
          <Section3>
            <Heading3>{t('seo:Houses for rent')}</Heading3>
            <Paragraph>
              <Trans i18nKey="seo:Houses for rent Paragraph">
                There are plenty of{' '}
                <LinkText href={links.house}>houses for rent</LinkText> across
                Canada for those with a higher budget, need extra space, or who
                have multiple roommates. Both partial houses and entire houses
                for rent are common, as well as both new builds and older homes.
                Some areas, like Toronto and Vancouver, are also known for an
                abundance of laneway homes and townhomes, making it easy to find
                a place to rent that suits your needs.
              </Trans>
            </Paragraph>
          </Section3>
          <Section3>
            <Heading3>{t('seo:Townhouses for rent')}</Heading3>
            <Paragraph>
              <Trans i18nKey="seo:Townhouses for rent Paragraph">
                Townhouses are a popular form of housing that combine the best
                qualities of both apartments and whole houses. Typically less
                expensive than houses,{' '}
                <LinkText href={links.townhouse}>townhouses for rent</LinkText>{' '}
                offer an attractive combination of freedom and space, generally
                coming with your own private yard and entrance.
              </Trans>
            </Paragraph>
          </Section3>
          <Section3>
            <Heading3>{t('seo:Basements for rent')}</Heading3>
            <Paragraph>
              <Trans i18nKey="seo:Basements for rent Paragraph">
                For those looking to find affordable rental housing in this
                increasingly expensive city, basements for rent pose an
                attractive alternative. Since house prices are so expensive,
                homeowners will sometimes rent out their basements as a way to
                offset their mortgage payments. This makes it easy for renters
                to find relatively inexpensive basements for rent in areas
                throughout the country.
              </Trans>
            </Paragraph>
          </Section3>
          <Section3>
            <Heading3>{t('seo:Condos for rent')}</Heading3>
            <Paragraph>
              <Trans i18nKey="seo:Condos for rent Paragraph">
                In more urban areas like Vancouver and Toronto,{' '}
                <LinkText href={links.apartment}>condos for rent</LinkText> are
                a common sight. These types of rental accommodation are perfect
                for those who like to be in the centre of the action, and don’t
                need as much space. If you are looking to rent in the city
                centre though, be prepared for higher-than-average rent prices.
              </Trans>
            </Paragraph>
          </Section3>
          <Section3>
            <Heading3>{t('seo:Rooms for rent')}</Heading3>
            <Paragraph>
              <Trans i18nKey="seo:Rooms for rent Paragraph">
                Sharing a house or apartment is practically essential for those
                looking for cheap rent.{' '}
                <LinkText href={links.room}>Rooms for rent</LinkText> are a
                great way to experience wherever you’re living on a budget, and
                can offer you the opportunity to live in neighbourhoods that
                would otherwise be unaffordable.
              </Trans>
            </Paragraph>
          </Section3>
        </>
      )
    }
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Apartments for rent LOCATION Paragraph">
              In {{ cityName }}, apartments are a common form of housing you’ll
              find. Average rent prices for apartments depend on where you’re
              looking.{' '}
              <LinkText href={links.apartment}>Apartments for rent</LinkText> in
              urban areas are typically more expensive due to higher demand.
              Apartments generally require much less maintenance than houses,
              making them ideal for renters who don’t need as much space.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Houses for rent LOCATION Paragraph">
              There are plenty of{' '}
              <LinkText href={links.house}>houses for rent</LinkText> in{' '}
              {{ cityName }} for those with a higher budget, need extra space,
              or who have multiple roommates. Both partial houses and entire
              houses for rent are common, as well as both new builds and older
              homes. Some areas are also known for an abundance of laneway homes
              and townhomes, making it easy to find a place to rent that suits
              your needs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Townhouses for rent LOCATION Paragraph">
              Townhouses are another popular form of housing in {{ cityName }}{' '}
              that combine the best qualities of both apartments and whole
              houses. Typically less expensive than houses,{' '}
              <LinkText href={links.townhouse}>townhouses for rent</LinkText>{' '}
              offer an attractive combination of freedom and space, generally
              coming with your own private yard and entrance.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Basements for rent LOCATION Paragraph">
              For those looking to find affordable rental housing in{' '}
              {{ cityName }}, basements for rent pose an attractive alternative.
              Since house prices are so expensive, homeowners will sometimes
              rent out their basements as a way to offset their mortgage
              payments. This makes it easy for renters to find relatively
              inexpensive basements for rent in areas throughout the country.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Condos for rent LOCATION Paragraph">
              <LinkText href={links.apartment}>Condos for rent</LinkText> are
              also a common sight in {{ cityName }}. These types of rental
              accommodation are perfect for those who like to be in the centre
              of the action, and don’t need as much space. If you are looking to
              rent in the city centre though, be prepared for
              higher-than-average rent prices.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Rooms for rent LOCATION Paragraph">
              Sharing a house or apartment is practically essential for those
              looking for cheap rent in {{ cityName }}.{' '}
              <LinkText href={links.room}>Rooms for rent</LinkText> are a great
              way to experience wherever you’re living on a budget, and can
              offer you the opportunity to live in neighbourhoods that would
              otherwise be unaffordable.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    if (isCanadaPage) {
      return (
        <>
          <Section3>
            <Heading3>{t('seo:Pet friendly rentals')}</Heading3>
            <Paragraph>
              <Trans i18nKey="seo:Pet friendly rentals Paragraph">
                Though not every landlord is keen on allowing pets, there are a
                good number of{' '}
                <LinkText href={links.petFriendly}>
                  pet-friendly rentals across Canada
                </LinkText>{' '}
                for those with furry friends. Both{' '}
                <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
                <LinkText href={links.catFriendly}>cat-friendly</LinkText>{' '}
                rental units are available throughout the country, as well as
                properties where both are allowed. Try searching for
                pet-friendly units using search filters on your rental platform
                to find units that match your needs.
              </Trans>
            </Paragraph>
          </Section3>
          <Section3>
            <Heading3>{t('seo:Furnished rentals')}</Heading3>
            <Paragraph>
              <Trans i18nKey="seo:Furnished rentals Paragraph">
                If you’re new to town or just haven’t had a chance to pick up
                furniture of your own yet, not to worry – there are plenty of{' '}
                <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
                available in cities across Canada. Renters seeking furnished
                units should search for these units using filters, but do be
                prepared to pay more in rent. There are{' '}
                <LinkText
                  href={
                    isChinese
                      ? 'https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e7%a7%9f%e5%ae%a2%e9%a1%bb%e7%9f%a5%ef%bc%9a%e5%87%ba%e7%a7%9f%e5%8d%95%e4%bd%8d%e5%b8%a6%e5%ae%b6%e5%85%b7%e8%bf%98%e6%98%af%e4%b8%8d%e5%b8%a6%e5%ae%b6%e5%85%b7-%e7%a7%9f%e9%87%91%e5%8c%ba%e5%88%ab/'
                      : 'https://liv.rent/blog/renters/renting-furnished-unfurnished/'
                  }
                >
                  pros and cons to renting furnished units compared to
                  unfurnished units
                </LinkText>
                , so deciding whether it’s worth it to buy furniture when moving
                is a good first step.
              </Trans>
            </Paragraph>
          </Section3>
          <Section3>
            <Heading3>{t('seo:Short term rentals')}</Heading3>
            <Paragraph>
              <Trans i18nKey="seo:Short term rentals Paragraph">
                Canada has plenty of work opportunities and world-class academic
                institutions, making it a popular place for those renting on a
                short-term basis.{' '}
                <LinkText href={links.shortTerm}>Short-term rentals</LinkText>{' '}
                are exceedingly popular in most cities, particularly during the
                summer months and at the beginning of school semesters. The
                added flexibility is an appealing prospect for many renters, and
                a key factor in{' '}
                <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                  deciding whether or not short-term rentals are right for you
                </LinkText>
                .
              </Trans>
            </Paragraph>
          </Section3>
          <Section3>
            <Heading3>{t('seo:Cheap rentals')}</Heading3>
            <Paragraph>
              <Trans i18nKey="seo:Cheap rentals Paragraph">
                Canadian cities like{' '}
                <LinkText
                  href={
                    isChinese
                      ? 'https://liv.rent/blog/zh/%E7%B2%BE%E5%93%81%E6%88%BF%E6%BA%90/2020%E7%89%88%E5%A4%A7%E6%B8%A9%E5%93%A5%E5%8D%8E1000%E6%88%96%E4%BB%A5%E4%B8%8B%E5%8F%AF%E4%BB%A5%E7%A7%9F%E5%88%B0%E4%BB%80%E4%B9%88%E6%88%BF%EF%BC%9F/'
                      : 'https://liv.rent/blog/neighbourhoods/what-can-1800-in-rent-get-you-in-metro-vancouver/'
                  }
                >
                  Vancouver
                </LinkText>{' '}
                and{' '}
                <LinkText
                  href={
                    isChinese
                      ? 'https://liv.rent/blog/zh/%E7%A7%9F%E5%AE%A2/1800-%E6%9C%88%E5%9C%A8%E5%A4%A7%E5%A4%9A%E4%BC%A6%E5%A4%9A%E7%A7%9F%E4%BB%80%E4%B9%88%E6%88%BF%EF%BC%9F/'
                      : 'https://liv.rent/blog/neighbourhoods/what-can-1800-in-rent-get-you-in-toronto/'
                  }
                >
                  Toronto
                </LinkText>{' '}
                aren’t known for inexpensive rent, but if you’re careful in your
                search, it shouldn’t be too hard to find something within your
                budget. If you’re looking for{' '}
                <LinkText
                  href={
                    isChinese
                      ? 'https://liv.rent/blog/zh/liv%E7%A7%9F%E6%88%BF/%E5%9C%A8%E5%8A%A0%E6%8B%BF%E5%A4%A71000-%E6%9C%88%E8%83%BD%E7%A7%9F%E5%88%B0%E4%BB%80%E4%B9%88%E6%A0%B7%E7%9A%84%E6%88%BF/'
                      : 'https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/'
                  }
                >
                  cheap rentals in Canada
                </LinkText>
                , try finding a roommate or two and exploring shared housing; or
                search for <LinkText href={links.room}>rooms for rent</LinkText>
                . It’s worth noting that in every city, there are neighbourhoods
                where rent is considerably cheaper, so exploring these options
                is a good idea as well.
              </Trans>
            </Paragraph>
          </Section3>
        </>
      )
    }

    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals LOCATION Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>
                cat-friendly rental units
              </LinkText>{' '}
              are available, as well as properties where both are allowed. Try
              searching for pet-friendly units using search filters on your
              rental platform to find units that match your needs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ cityName }}. Renters seeking furnished units
              should search for these units using filters, but do be prepared to
              pay more in rent. There are{' '}
              <LinkText
                href={
                  isChinese
                    ? 'https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e7%a7%9f%e5%ae%a2%e9%a1%bb%e7%9f%a5%ef%bc%9a%e5%87%ba%e7%a7%9f%e5%8d%95%e4%bd%8d%e5%b8%a6%e5%ae%b6%e5%85%b7%e8%bf%98%e6%98%af%e4%b8%8d%e5%b8%a6%e5%ae%b6%e5%85%b7-%e7%a7%9f%e9%87%91%e5%8c%ba%e5%88%ab/'
                    : 'https://liv.rent/blog/renters/renting-furnished-unfurnished/'
                }
              >
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Short term rentals LOCATION Paragraph">
              {{ cityName }} has plenty of work opportunities and excellent
              academic institutions, making it a popular place for those renting
              on a short-term basis.{' '}
              <LinkText href={links.shortTerm}>Short-term rentals</LinkText> are
              exceedingly popular in most cities, particularly during the summer
              months and at the beginning of school semesters. The added
              flexibility is an appealing prospect for many renters, and a key
              factor in{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                deciding whether or not short-term rentals are right for you
              </LinkText>
              .
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Cheap rentals LOCATION Generic Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in {{ cityNameWithState }}
              </LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that in {{ cityName }}, there are neighbourhoods
              where rent is considerably cheaper, so exploring these options is
              a good idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getAmenities = () => {
    return (
      <>
        <Paragraph>
          {hasFilter ? (
            <Trans i18nKey="seo:Rental Amenities FILTER in LOCATION Paragraph 1">
              When you’re searching for{' '}
              <LinkText href={`${links.base}/${filterSlug}`}>
                {{ filterName }} in {{ cityName }}
              </LinkText>
              , make sure to look at what amenities are offered. For instance,
              check to see if the rental includes utilities like air
              conditioning, cable and internet or features such as a dishwasher,
              parking, balcony and fridge.
            </Trans>
          ) : (
            <Trans i18nKey="seo:Rental Amenities LOCATION Paragraph 1">
              When you’re searching for an{' '}
              <LinkText href={links.apartment}>
                apartment to rent in {{ cityName }}
              </LinkText>
              , make sure to look at what amenities are offered. For instance,
              check to see if the rental includes utilities like air
              conditioning, cable and internet or features such as a dishwasher,
              parking, balcony and fridge.
            </Trans>
          )}
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Rental Amenities LOCATION Paragraph 2">
            Tip: When looking at listings on liv.rent, scroll down to the
            &quot;Amenities&quot; section to see what utilities and features are
            included with the rental.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getLiving = () => {
    return (
      <>
        {isCanadaPage ? (
          <Trans i18nKey="seo:Living in Canada Paragraph">
            <Paragraph>
              People from around the world look to Canada as a safe haven. Known
              for its natural beauty, multiculturalism, universal healthcare,
              and an open-minded citizenry, Canada is a coveted destination for
              newcomers. The three largest cities —{' '}
              <LinkText href="/rental-listings/city/toronto">Toronto</LinkText>,{' '}
              <LinkText href="/rental-listings/city/montreal">
                Montreal
              </LinkText>{' '}
              and{' '}
              <LinkText href="/rental-listings/city/vancouver">
                Vancouver
              </LinkText>{' '}
              continue to attract 60% of overall immigrants; this is followed by
              mid-sized cities like{' '}
              <LinkText href="/rental-listings/city/calgary">Calgary</LinkText>{' '}
              and{' '}
              <LinkText href="/rental-listings/city/winnipeg">
                Winnipeg
              </LinkText>
              .
            </Paragraph>
            <Paragraph>
              If you’re not sure which Canadian city is right for you, check out
              some of these resources comparing the different cities.
            </Paragraph>
          </Trans>
        ) : (
          <Trans i18nKey="seo:Living in LOCATION Paragraph">
            <Paragraph>
              People from around the world look to Canada as a safe haven, and{' '}
              {{ cityName }} is one of those cities that truly has something for
              everyone.
            </Paragraph>
            <Paragraph>
              If you’re not sure that {{ cityName }} is the right city for you,
              check out some of these resources comparing the different cities.
            </Paragraph>
          </Trans>
        )}
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e5%8a%a0%e6%8b%bf%e5%a4%a7%e7%95%99%e5%ad%a65%e5%a4%a7%e5%9f%8e%e5%b8%82-%e7%a7%9f%e6%88%bf%e3%80%81%e5%ad%a6%e8%b4%b9%e3%80%81%e5%b7%a5%e4%bd%9c%e3%80%81%e7%ad%be%e8%af%81%e8%af%a6%e8%a7%a3/">
              加拿大最佳移民城市
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e6%b8%a9%e5%93%a5%e5%8d%8e%e3%80%81%e5%a4%9a%e4%bc%a6%e5%a4%9a%e6%af%94%e8%be%83/">
              温哥华 vs 多伦多：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%E8%A1%97%E5%8C%BA%E6%8C%87%E5%8D%97/%E6%B8%A9%E5%93%A5%E5%8D%8E%E5%92%8C%E7%B4%A0%E9%87%8C%E5%B1%85%E4%BD%8F%E6%88%90%E6%9C%AC%E6%AF%94%E8%BE%83/">
              温哥华 vs 素里：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/toronto-zh/2021%e7%89%88%e5%a4%9a%e4%bc%a6%e5%a4%9a%e3%80%81%e8%92%99%e7%89%b9%e5%88%a9%e5%b0%94%e7%94%9f%e6%b4%bb%e6%88%90%e6%9c%ac%e6%af%94%e8%be%83/">
              多伦多 vs 蒙特利尔：生活成本比较
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/2020/11/best-canadian-cities-for-immigration/">
              Best Canadian cities for immigration
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2019/11/vancouver-or-toronto-a-cost-of-living-comparison/">
              Vancouver vs Toronto: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/02/surrey-vs-vancouver-bc-comparison/">
              Vancouver vs Surrey: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/04/toronto-vs-montreal-cost-of-living-comparison/">
              Toronto vs Montreal: Cost of living comparison
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getPopularHoods = () => {
    if (isCanadaPage) {
      return (
        <>
          <Paragraph>
            <Trans i18nKey="seo:Popular Neighbourhoods Canada Paragraph 1">
              For those seeking to experience the very best that Canada has to
              offer, there are a number of neighbourhoods across the country
              that renters may want to look into.{' '}
              <LinkText href="https://liv.rent/rental-listings/city/vancouver">
                Vancouver
              </LinkText>{' '}
              and{' '}
              <LinkText href="https://liv.rent/rental-listings/city/toronto">
                Toronto
              </LinkText>{' '}
              are likely the two Canadian cities renters will be most familiar
              with, but cities like{' '}
              <LinkText href="https://liv.rent/rental-listings/city/montreal">
                Montreal
              </LinkText>
              ,{' '}
              <LinkText href="https://liv.rent/rental-listings/city/calgary">
                Calgary
              </LinkText>
              , and{' '}
              <LinkText href="https://liv.rent/rental-listings/city/edmonton">
                Edmonton
              </LinkText>{' '}
              each offer a similarly excellent standard of living with
              comparatively cheaper average rent prices. In Vancouver, renters
              tend to concentrate around the city’s Downtown, although areas
              like the West End and Kitsilano are also exceedingly popular. In
              nearby{' '}
              <LinkText href="https://liv.rent/rental-listings/city/burnaby">
                Burnaby
              </LinkText>
              , Brentwood and Metrotown are two popular and well-connected
              neighbourhoods.
            </Trans>
          </Paragraph>
          <Paragraph>
            <Trans i18nKey="seo:Popular Neighbourhoods Canada Paragraph 2">
              Heading east,{' '}
              <LinkText href="https://liv.rent/rental-listings/city/calgary">
                Calgary
              </LinkText>{' '}
              has become a popular destination for renters seeking more
              affordable rates, with areas like Beltline, Cityscape and Downtown
              among the most desirable locations. In{' '}
              <LinkText href="https://liv.rent/rental-listings/city/toronto">
                Toronto
              </LinkText>
              , Downtown, The Annex, Kensington Market and the Entertainment
              District are all excellent choices for renters seeking to
              experience the best of the city in a central location.{' '}
              <LinkText href="https://liv.rent/rental-listings/city/montreal">
                Montreal
              </LinkText>{' '}
              is known across the country for its vibrant cultural scene and has
              some great areas for renters to call home as well, like The
              Plateau-Mont-Royal and Downtown.
            </Trans>
          </Paragraph>
        </>
      )
    }
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo:Popular Neighbourhoods LOCATION Paragraph">
            {{ cityName }} is made up of smaller neighbourhoods with their own
            distinct features. For more information, you can check out the blog
            to learn about the different{' '}
            <LinkText
              href={
                isChinese
                  ? 'https://liv.rent/blog/zh/%E8%A1%97%E5%8C%BA%E6%8C%87%E5%8D%97/'
                  : 'https://liv.rent/blog/neighbourhoods/'
              }
            >
              neighbourhoods in {{ cityName }}
            </LinkText>
            .
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getLandmarks = () => {
    if (isCanadaPage) {
      return (
        <Paragraph>
          <Trans i18nKey="seo:Local Landmarks Canada Paragraph">
            Canada is known for its impressive natural beauty and diverse
            culture from coast-to-coast. Some of the most popular local
            landmarks in Canada include{' '}
            <LinkText href="/rental-listings/city/niagara-falls">
              Niagara Falls
            </LinkText>
            , located in the Niagara region of Ontario, about a two-hour drive
            from{' '}
            <LinkText href="/rental-listings/city/toronto">
              Downtown Toronto
            </LinkText>
            . Within Toronto itself, the CN Tower is a skyline-defining presence
            in the heart of the city and draws in large crowds nearly every day.
            British Columbia and{' '}
            <LinkText href="/rental-listings/city/vancouver">
              Vancouver
            </LinkText>{' '}
            in particular are hugely popular destinations among visitors to
            Canada, with landmarks such as Stanley Park in Vancouver itself and
            Capilano Suspension Bridge in{' '}
            <LinkText href="/rental-listings/city/north-vancouver">
              North Vancouver
            </LinkText>{' '}
            both extremely popular. The Rocky Mountain Range is another
            bucket-list trip for many, stretching from B.C. into Alberta and
            accessible by car from both{' '}
            <LinkText href="/rental-listings/city/calgary">Calgary</LinkText>{' '}
            and{' '}
            <LinkText href="/rental-listings/city/edmonton">Edmonton</LinkText>,
            though the drive is fairly long.
          </Trans>
        </Paragraph>
      )
    }
    return (
      <Paragraph>
        <Trans i18nKey="seo:Local Landmarks LOCATION Paragraph">
          Canada is known for its impressive natural beauty and diverse culture
          from coast-to-coast. You can find a full list of cultural landmarks
          and attractions in {{ cityName }} on{' '}
          <LinkText href="https://www.canada.ca/en/services/culture/cultural-attractions.html">
            Canada.ca
          </LinkText>
          .
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: getAmenities(),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default GenericCityContent
